/**
 * Sam's drafts, laid out for the owner: search content shown as a Google result preview with each
 * value to copy, a website fix as plain steps with the code folded, and a Business Profile post as
 * it will look. His `change` field is markdown in which every value to paste is its own fenced code
 * block under a short label line (gsc-content-gap/method.md); this splits it back into those parts.
 * The search numbers come from the reads the draft cites, phrased in plain words here.
 */

import type { SearchTermData } from '../types';

/** Sam's drafts with their own layout. */
export const SAM_PANEL_SKILLS: ReadonlySet<string> = new Set(['gsc_content_gap', 'site_technical_fix', 'gbp_post']);

export type ChangePart =
  | { kind: 'prose'; text: string }
  | { kind: 'value'; label: string; value: string; lang: string; page: string | null };

export interface ParsedChange {
  /** The draft's one-line summary: its first prose line. */
  summary: string | null;
  parts: ChangePart[];
}

const FENCE = /^\s*```\s*([\w-]*)\s*$/;
const HEADING = /^\s*#{1,6}\s+(.+?)\s*#*\s*$/;
/** A label line: short, no sentence inside it, e.g. "Title", "**Meta description:**". */
const LABEL_MAX = 60;

function plainLabel(line: string): string {
  return line
    .replace(/^[\s>*_-]+|[\s*_]+$/g, '')
    .replace(/\*\*|__/g, '')
    .replace(/:\s*$/, '')
    // "Title tag for https://site/page" names the page, which the heading above already does.
    .replace(/\s+(?:for|on)\s+https?:\/\/\S+$/i, '')
    .trim();
}

function looksLikeLabel(line: string): boolean {
  const label = plainLabel(line);
  // A line ending in a colon introduces the block even when it is long ("Title tag for <address>:").
  const short = label.length <= LABEL_MAX || (/:\s*\**\s*$/.test(line) && line.length <= 160);
  return label.length > 0 && short && !/[.!?](\s|$)/.test(label) && !/^\d+[.)]\s/.test(label);
}

/** Splits a change into prose and labelled values, in order. */
export function parseChange(markdown: string): ParsedChange {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const parts: ChangePart[] = [];
  let prose: string[] = [];
  let page: string | null = null;
  const flushProse = () => {
    const text = prose.join('\n').trim();
    if (text) parts.push({ kind: 'prose', text });
    prose = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const fence = FENCE.exec(lines[i]!);
    if (fence) {
      const code: string[] = [];
      let j = i + 1;
      while (j < lines.length && !FENCE.test(lines[j]!)) code.push(lines[j++]!);
      i = j;
      // The label is the last prose line before the block, when it reads as one.
      let label = '';
      while (prose.length && !prose[prose.length - 1]!.trim()) prose.pop();
      const last = prose[prose.length - 1];
      if (last !== undefined && looksLikeLabel(last) && !HEADING.test(last)) {
        label = plainLabel(last);
        prose.pop();
      }
      flushProse();
      parts.push({ kind: 'value', label, value: code.join('\n').trim(), lang: fence[1] ?? '', page });
      continue;
    }
    const heading = HEADING.exec(lines[i]!);
    if (heading) {
      flushProse();
      page = plainLabel(heading[1]!);
      parts.push({ kind: 'prose', text: lines[i]!.trim() });
      continue;
    }
    prose.push(lines[i]!);
  }
  flushProse();
  const firstProse = parts.find((p): p is Extract<ChangePart, { kind: 'prose' }> => p.kind === 'prose' && !HEADING.test(p.text));
  const summary = firstProse ? firstProse.text.split('\n').find((l) => l.trim())?.trim() ?? null : null;
  return { summary, parts };
}

const TITLE_LABEL = /\btitle\b/i;
const DESCRIPTION_LABEL = /\bdescription\b/i;

export interface SearchPreview {
  title: string;
  description: string;
}

/**
 * The title and description to show as a Google result: the values labelled so, else a pair of
 * unlabelled values shaped like one (a short line, then a longer one).
 */
export function searchPreview(parts: ChangePart[]): SearchPreview | null {
  const values = parts.filter((p): p is Extract<ChangePart, { kind: 'value' }> => p.kind === 'value');
  const title = values.find((v) => TITLE_LABEL.test(v.label));
  const description = values.find((v) => DESCRIPTION_LABEL.test(v.label));
  if (title && description) return { title: title.value, description: description.value };
  const unlabelled = values.filter((v) => !v.label);
  if (!title && !description && unlabelled.length >= 2) {
    const [a, b] = unlabelled as [Extract<ChangePart, { kind: 'value' }>, Extract<ChangePart, { kind: 'value' }>];
    if (!a.value.includes('\n') && !b.value.includes('\n') && a.value.length <= 75 && b.value.length > a.value.length && b.value.length <= 200) {
      return { title: a.value, description: b.value };
    }
  }
  return null;
}

/** A plain name for an unlabelled value, from what it looks like. */
export function valueLabel(part: Extract<ChangePart, { kind: 'value' }>, preview: SearchPreview | null): string {
  if (part.label) return part.label;
  if (preview && part.value === preview.title) return 'Page title';
  if (preview && part.value === preview.description) return 'Description';
  if (/^\s*</.test(part.value)) return 'Code';
  if (part.lang === 'json') return 'Structured data';
  return 'Text to copy';
}

/** Roughly where Google stops showing a title or description in a result. */
export function previewLimit(label: string): number | null {
  if (TITLE_LABEL.test(label)) return 60;
  if (DESCRIPTION_LABEL.test(label)) return 155;
  return null;
}

/** "about 1 in 500", "3%", "nobody": a click rate in plain words. */
export function clickRateWords(term: Pick<SearchTermData, 'clicks' | 'ctr' | 'impressions'>): string {
  if (term.clicks === 0 || term.ctr === null || term.ctr === 0) return 'nobody';
  if (term.ctr < 0.01) {
    const oneIn = 1 / term.ctr;
    const nice = oneIn >= 100 ? Math.round(oneIn / 50) * 50 : Math.round(oneIn / 10) * 10;
    return `about 1 in ${Math.max(nice, 10)}`;
  }
  return `${Math.round(term.ctr * 1000) / 10}%`;
}

/** "17.4": an average position to one decimal, as Search Console shows it. */
export function formatPosition(position: number): string {
  return String(Math.round(position * 10) / 10);
}

/** "page 2": the Google results page an average position falls on. */
export function resultsPage(position: number): string {
  return `page ${Math.max(1, Math.ceil(position / 10))}`;
}

function pageName(url: string | null): string {
  if (!url) return 'your site';
  try {
    const path = new URL(url).pathname.replace(/\/+$/, '');
    if (!path) return 'your home page';
    const last = path.split('/').pop()!.replace(/[-_]+/g, ' ');
    return `your ${last} page`;
  } catch {
    return 'your site';
  }
}

export interface SamTake {
  sentences: string;
  tiles: Array<{ label: string; value: string; note: string }>;
}

/** Sam's take on a search term, from its numbers. `closing` is the skill's own last sentence. */
export function searchTermTake(term: SearchTermData, closing: string): SamTake {
  const where = term.avgPosition !== null ? `, mostly on ${resultsPage(term.avgPosition)} of Google,` : '';
  const clicked = clickRateWords(term);
  const clickedSentence = clicked === 'nobody' ? 'nobody clicked' : `${clicked} of the people who saw it clicked`;
  return {
    sentences: `${capitalise(pageName(term.pageUrl))} showed up ${term.impressions.toLocaleString('en-US')} times for "${term.term}"${where} and ${clickedSentence}. ${closing}`.trim(),
    tiles: [
      { label: 'Times shown', value: term.impressions.toLocaleString('en-US'), note: `for "${term.term}"` },
      { label: 'Where you show up', value: term.avgPosition === null ? 'Not yet' : formatPosition(term.avgPosition), note: term.avgPosition === null ? 'no average yet' : `average spot: ${resultsPage(term.avgPosition)}` },
      { label: 'People who clicked', value: clicked === 'nobody' ? '0' : clicked.replace(/^about /, ''), note: clicked === 'nobody' ? 'none yet' : `${term.clicks.toLocaleString('en-US')} ${term.clicks === 1 ? 'click' : 'clicks'}` },
    ],
  };
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Where a page address shows in a Google result: "site.com › services › emergency-dentistry". */
export function breadcrumb(url: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return [u.hostname.replace(/^www\./, ''), ...u.pathname.split('/').filter(Boolean)].join(' › ');
  } catch {
    return null;
  }
}
